import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, CurrencyDollar, PencilSimpleLine, Trash } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { formatDollars } from '@/utils'

import Dashboard from '../../Dashboard'
import RadioField from '../../FormLegacy/FormFieldsV2/RadioField'
import Loader from '../../Loader/Loader'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import API from '../../../api/api'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'

import BillDetailsModal from './BillDetailsModal'
import YardiBillList from './YardiBillList'
import StripePaymentMethodModal from './StripePaymentMethodModal'
import { BankCardAddIcon, CardBrandIcon, StripeIcon } from './PaymentIcons'
import { allocateAmount, getChargePayable, getProcessingFeeCents, methodLabel } from './helpers'

const PayNowView = ({
  currentUser,
  isYardi,
  yardiTransactions,
  selectedTransactions,
  editTransaction,
  totalPay,
  errorMessage,
  onClose,
  onEditPrice,
  handleChangeValue,
  saveNewValue,
  getOriginalValue,
  manageSelectedTransaction,
  paymentSettings,
  customTotal,
  refetchTransactions,
}) => {
  const { displaySnackbar } = useSnackbar()
  const { t } = useTranslation(['organisation', 'common'])
  const [isLoading, setLoading] = useState(false)
  const [transactionDetails, setTransactionDetails] = useState(null)
  const [isPayView, setPayView] = useState(false)
  const [processingFee, setProcessingFee] = useState(null)
  const [isLoadingFee, setLoadingFee] = useState(false)
  const [payType, setPayType] = useState('Credit Card')
  const [paymentMethods, setPaymentMethods] = useState([])
  const [isLoadingMethods, setLoadingMethods] = useState(false)
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null)
  const [isNewCreditCard, setIsNewCreditCard] = useState(false)
  const [isNewBankAccount, setIsNewBankAccount] = useState(false)
  const [isMethodModalOpen, setMethodModalOpen] = useState(false)
  const [methodToDelete, setMethodToDelete] = useState(null)
  const [isDeletingMethod, setIsDeletingMethod] = useState(false)

  const buildingId = currentUser ? currentUser.buildingId.id : null
  const cardMethods = paymentMethods.filter((method) => method.methodType !== 'ACH')
  const bankMethods = paymentMethods.filter((method) => method.methodType === 'ACH')
  const isAddingNewMethod = payType === 'Bank Account' ? isNewBankAccount : isNewCreditCard
  const isSusbmitPaymentEnabled =
    !isAddingNewMethod &&
    !!selectedPaymentMethodId &&
    !(isYardi && isLoadingFee) &&
    !(isYardi && !selectedTransactions.length)

  const getSummary = () => {
    if (isYardi && !!customTotal) {
      return customTotal
    } else if (yardiTransactions.length) {
      return yardiTransactions?.reduce((acc, charge) => {
        if (selectedTransactions.includes(charge.chargeCode)) {
          return acc + (charge.fixedPrice || getChargePayable(charge))
        }
        return acc
      }, 0)
    }

    return 0
  }

  const getTotalAmount = () => {
    let totalAmount = Number(getSummary())
    return Number(totalAmount.toFixed(2))
  }

  const getProcessingFeeAmount = () =>
    getProcessingFeeCents(Math.round(getTotalAmount() * 100), processingFee) / 100

  const getSummaryAmount = () => {
    return Number((getTotalAmount() + getProcessingFeeAmount()).toFixed(2))
  }

  const getBillsLength = () => {
    if (isYardi) {
      return selectedTransactions.length
    }
  }

  const DEFAULT_ACH_PROCESSING_FEE = { type: 'ach', mode: 'percent', percentage: 0.8 }

  const getProcessingFeeYardi = async () => {
    const defaultFee = payType === 'Bank Account' ? DEFAULT_ACH_PROCESSING_FEE : null
    setLoadingFee(true)
    try {
      const { data } = await API.getYardiProcessingFee(buildingId)
      const selectedType = payType === 'Bank Account' ? 'ach' : 'card'
      const selectedFee = data.find((obj) => obj.type === selectedType)
      setProcessingFee(selectedFee || defaultFee)
    } catch {
      setProcessingFee(defaultFee)
    } finally {
      setLoadingFee(false)
    }
  }

  const fetchPaymentMethods = async () => {
    setLoadingMethods(true)
    try {
      const { data } = await API.getPaymentMethods()
      const methods = data || []
      setPaymentMethods(methods)
      return methods
    } catch {
      setPaymentMethods([])
      return []
    } finally {
      setLoadingMethods(false)
    }
  }

  const handleMethodSaved = async () => {
    const existingIds = paymentMethods.map((method) => method.id)
    setMethodModalOpen(false)
    const refreshedMethods = await fetchPaymentMethods()
    const newMethod = refreshedMethods.find((method) => !existingIds.includes(method.id))
    // preferredType only reorders the modal's tabs, so the resident may have saved a different
    // method type than the one that opened it — trust the saved method's real methodType, not
    // whatever payType was active before the modal opened.
    if (newMethod) {
      setPayType(newMethod.methodType === 'ACH' ? 'Bank Account' : 'Credit Card')
    }
    setSelectedPaymentMethodId(newMethod?.id || refreshedMethods[0]?.id || null)
    setIsNewCreditCard(false)
    setIsNewBankAccount(false)
    displaySnackbar(t('organisation:payments.toasts.paymentMethodSaved'))
  }

  const handleDeleteMethod = async () => {
    setIsDeletingMethod(true)
    try {
      await API.deletePaymentMethod(methodToDelete.id)
      const refreshedMethods = await fetchPaymentMethods()
      if (selectedPaymentMethodId === methodToDelete.id) {
        const isBank = methodToDelete.methodType === 'ACH'
        const remaining = refreshedMethods.filter((method) =>
          isBank ? method.methodType === 'ACH' : method.methodType !== 'ACH',
        )
        setSelectedPaymentMethodId(remaining[0]?.id || null)
        if (isBank) {
          setIsNewBankAccount(!remaining.length)
        } else {
          setIsNewCreditCard(!remaining.length)
        }
      }
      displaySnackbar(t('organisation:payments.toasts.paymentMethodDeleted'))
    } catch (error) {
      displaySnackbar(
        error?.response?.data?.message || t('organisation:payments.errors.deleteMethodFailed'),
        true,
      )
    } finally {
      setIsDeletingMethod(false)
      setMethodToDelete(null)
    }
  }

  // createRentPayment both creates and confirms the PaymentIntent server-side using
  // paymentMethodId — the client no longer needs to call stripe.confirmCardPayment.
  const submitPayment = async () => {
    if (!isYardi) return

    setLoading(true)

    const selectedCharges = yardiTransactions
      .filter((transactionObj) => selectedTransactions.includes(transactionObj.chargeCode))
      .map((transactionObj) => ({
        chargeCode: transactionObj.chargeCode,
        amount: Math.round((transactionObj.fixedPrice || getChargePayable(transactionObj)) * 100),
      }))
    // same condition as getSummary — charge the edited total the resident sees, not the full balance
    const chargeCodes = (
      customTotal ? allocateAmount(selectedCharges, Math.round(customTotal * 100)) : selectedCharges
    ).filter((charge) => charge.amount > 0)

    const billingAmount = chargeCodes.reduce((sum, charge) => sum + charge.amount, 0)
    const totalAmount = billingAmount + getProcessingFeeCents(billingAmount, processingFee)

    try {
      await API.createRentPayment({
        billingAmount,
        totalAmount,
        paymentMethodId: selectedPaymentMethodId,
        paymentType: payType === 'Bank Account' ? 'ACH' : 'CreditCard',
        chargeCodes,
      })
      displaySnackbar(t('organisation:payments.toasts.paymentSubmitted'))
      // refresh client/transactions so the resident sees the post-payment balance, not the stale one
      await refetchTransactions?.()
      setPayView(false)
      onClose()
    } catch (error) {
      displaySnackbar(
        error?.response?.data?.message ||
          error?.message ||
          t('organisation:payments.errors.submitFailed'),
        true,
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (buildingId && isPayView && isYardi) {
      getProcessingFeeYardi()
    }
  }, [payType, buildingId, isPayView, isYardi])

  useEffect(() => {
    if (currentUser) {
      fetchPaymentMethods().then((methods) => {
        const cards = methods.filter((method) => method.methodType !== 'ACH')
        const banks = methods.filter((method) => method.methodType === 'ACH')

        const autopayCard = methods.find(
          (method) => method.usedByAutoPay && method.methodType !== 'ACH',
        )
        setSelectedPaymentMethodId(autopayCard?.id || cards[0]?.id || null)
        setIsNewCreditCard(!cards.length)
        setIsNewBankAccount(!banks.length)
      })
    }
  }, [currentUser])

  if (isPayView) {
    return (
      <Dashboard.Container className="ctp-payments-pay-now">
        <div className="ctp-payments-pay-now__header">
          <button
            className={`button button-bordered ${isLoading ? 'is-loading' : ''}`}
            onClick={() => setPayView(false)}
          >
            <ArrowLeft size={20} />
            {t('common:actions.back')}
          </button>
          {t('organisation:payments.payNow.title')}
        </div>
        <div className="ctp-payments-pay-now__body">
          <div className="payment-methods-container">
            <div className="simple-header">
              {t('organisation:payments.payNow.choosePaymentMethodLabel')}
            </div>
            <div className="payment-method-wrap">
              <div className="flex-between">
                <RadioField
                  name="credit-card-radio"
                  value="Credit Card"
                  checked={payType === 'Credit Card'}
                  label={t('organisation:payments.payNow.creditCard')}
                  onChange={() => {
                    setPayType('Credit Card')
                    setSelectedPaymentMethodId(cardMethods[0]?.id || null)
                  }}
                />
                <div className="card-logos">
                  <img src="/images/Maestro.png" alt="Maestro" />
                  <img src="/images/visa.png" alt="Visa" />
                </div>
              </div>
              {payType === 'Credit Card' && (
                <div className="credit-card-options">
                  {isLoadingMethods ? (
                    <Loader style={{ width: 24, height: 24 }} />
                  ) : (
                    <>
                      <div className="options-divider" />
                      {!!cardMethods.length && (
                        <div className="saved-methods-list">
                          {cardMethods.map((method) => {
                            const isOnlyMethod = cardMethods.length === 1
                            return (
                              <div key={method.id} className="credit-card-option">
                                <div
                                  className={`credit-card-option__details ${
                                    !isOnlyMethod ? 'is-selectable' : ''
                                  } ${selectedPaymentMethodId === method.id ? 'is-selected' : ''}`}
                                  onClick={
                                    isOnlyMethod
                                      ? undefined
                                      : () => setSelectedPaymentMethodId(method.id)
                                  }
                                >
                                  <div className="credit-card-option__info">
                                    <CardBrandIcon brand={method.brand} />
                                    <span className="saved-method-summary__label">
                                      {methodLabel(t, method)}
                                    </span>
                                  </div>
                                  {!isOnlyMethod && (
                                    <RadioField
                                      name="saved-credit-card-choice"
                                      value={method.id}
                                      checked={selectedPaymentMethodId === method.id}
                                      label=""
                                      onChange={() => setSelectedPaymentMethodId(method.id)}
                                    />
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="delete-method-button"
                                  aria-label={t('organisation:payments.payNow.deleteMethod')}
                                  onClick={() => setMethodToDelete(method)}
                                >
                                  <Trash size={16} />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      )}
                      <button
                        type="button"
                        className="add-method-button"
                        onClick={() => setMethodModalOpen(true)}
                      >
                        <BankCardAddIcon />
                        {t('organisation:payments.payNow.addCard')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="payment-method-wrap">
              <div className="flex-between">
                <RadioField
                  name="bank-account-radio"
                  value="Bank Account"
                  checked={payType === 'Bank Account'}
                  label={t('organisation:payments.payNow.bankAccount')}
                  onChange={() => {
                    setPayType('Bank Account')
                    setSelectedPaymentMethodId(bankMethods[0]?.id || null)
                  }}
                />
                <div className="grey-header">
                  {t('organisation:payments.payNow.allBanksSupported')}
                </div>
              </div>
              {payType === 'Bank Account' && (
                <div className="credit-card-options">
                  {isLoadingMethods ? (
                    <Loader style={{ width: 24, height: 24 }} />
                  ) : (
                    <>
                      <div className="options-divider" />
                      {!!bankMethods.length && (
                        <div className="saved-methods-list">
                          {bankMethods.map((method) => {
                            const isOnlyMethod = bankMethods.length === 1
                            return (
                              <div key={method.id} className="credit-card-option">
                                <div
                                  className={`credit-card-option__details ${
                                    !isOnlyMethod ? 'is-selectable' : ''
                                  } ${selectedPaymentMethodId === method.id ? 'is-selected' : ''}`}
                                  onClick={
                                    isOnlyMethod
                                      ? undefined
                                      : () => setSelectedPaymentMethodId(method.id)
                                  }
                                >
                                  <div className="credit-card-option__info">
                                    <StripeIcon />
                                    <span className="saved-method-summary__label">
                                      {methodLabel(t, method)}
                                    </span>
                                  </div>
                                  {!isOnlyMethod && (
                                    <RadioField
                                      name="saved-bank-account-choice"
                                      value={method.id}
                                      checked={selectedPaymentMethodId === method.id}
                                      label=""
                                      onChange={() => setSelectedPaymentMethodId(method.id)}
                                    />
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="delete-method-button"
                                  aria-label={t('organisation:payments.payNow.deleteMethod')}
                                  onClick={() => setMethodToDelete(method)}
                                >
                                  <Trash size={16} />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      )}
                      <button
                        type="button"
                        className="add-method-button"
                        onClick={() => setMethodModalOpen(true)}
                      >
                        <BankCardAddIcon />
                        {t('organisation:payments.payNow.addBankAccount')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="summary-details">
            <div className="summary-details__line">
              <div className="header-name">{t('organisation:payments.payNow.totalBilling')}</div>
              <div className="header-name">{formatDollars(getSummary(), { zeroPrice: true })}</div>
            </div>
            {isYardi ? (
              <div className="summary-details__line">
                <div className="header-name">{t('organisation:payments.payNow.processingFee')}</div>
                <div className="header-name">
                  {processingFee != null
                    ? formatDollars(getProcessingFeeAmount(), { zeroPrice: true })
                    : t('organisation:payments.payNow.choosePaymentMethod')}
                </div>
              </div>
            ) : null}
            <div className="summary-details__space">
              <div className="header-name__bold">{t('organisation:payments.payNow.summary')}</div>
              <div className="header-name__bold">
                {formatDollars(getSummaryAmount(), { zeroPrice: true })}
              </div>
            </div>
            <button
              className={`button button-black ${isLoading ? 'is-loading' : ''}`}
              disabled={!isSusbmitPaymentEnabled || isLoading}
              onClick={submitPayment}
            >
              {t('organisation:payments.payNow.submitPayment')}
            </button>
          </div>
        </div>
        <StripePaymentMethodModal
          createSetupIntent={API.createSetupIntent}
          isVisible={isMethodModalOpen}
          preferredType={payType === 'Bank Account' ? 'us_bank_account' : 'card'}
          onClose={() => setMethodModalOpen(false)}
          onSaved={handleMethodSaved}
        />
        {methodToDelete && (
          <ConfirmPopup
            isOpen
            className={methodToDelete.usedByAutoPay ? 'is-caution' : ''}
            title={t('common:confirmPopup.confirmation')}
            body={t(
              methodToDelete.usedByAutoPay
                ? 'organisation:payments.payNow.deleteMethodAutopayWarning'
                : 'organisation:payments.payNow.deleteMethodBody',
              { methodLabel: methodLabel(t, methodToDelete) },
            )}
            confirmText={t('common:actions.delete')}
            cancelText={t('common:actions.cancel')}
            isLoading={isDeletingMethod}
            confirmCallback={handleDeleteMethod}
            cancelCallback={() => setMethodToDelete(null)}
          />
        )}
      </Dashboard.Container>
    )
  }

  return (
    <Dashboard.Container className="ctp-payments-pay-now">
      <div className="ctp-payments-pay-now__header">
        <button className="button button-bordered" onClick={onClose}>
          <ArrowLeft size={20} />
          {t('common:actions.back')}
        </button>
        {t('organisation:payments.payNow.title')}
      </div>
      <div className="ctp-payments-pay-now__body">
        {isYardi && (
          <YardiBillList
            transactions={yardiTransactions}
            selectedTransactions={selectedTransactions}
            editTransaction={editTransaction}
            totalPay={totalPay}
            errorMessage={errorMessage}
            onEditPrice={onEditPrice}
            handleChangeValue={handleChangeValue}
            saveNewValue={saveNewValue}
            getOriginalValue={getOriginalValue}
            setTransactionDetails={setTransactionDetails}
            manageSelectedTransaction={manageSelectedTransaction}
            paymentSettings={paymentSettings}
          />
        )}
        <div className="summary-details">
          <div className="summary-details__line">
            <div className="header-name">{t('organisation:payments.payNow.bills')}</div>
            <div className="header-name">{getBillsLength()}</div>
          </div>
          <div className="summary-details__line">
            <div className="header-name">{t('organisation:payments.payNow.totalBilling')}</div>
            {isYardi &&
            !paymentSettings?.allowChargesSelection &&
            paymentSettings?.allowPartialPayment ? (
              editTransaction && editTransaction.chargeCode === 'total' ? (
                <div className="row-flex">
                  <CurrencyDollar size={16} className="currency-icon" />
                  <TextField
                    name="pariceValue"
                    type="number"
                    onChange={handleChangeValue}
                    value={totalPay}
                    style={{ width: '100%' }}
                    isError={!!errorMessage}
                    errorLabel={errorMessage}
                  />
                  <button className="button button-bordered" onClick={getOriginalValue}>
                    {t('organisation:payments.billList.payInFull')}
                  </button>
                  <button
                    className="button button-black"
                    onClick={saveNewValue}
                    disabled={!!errorMessage}
                  >
                    {t('common:actions.save')}
                  </button>
                </div>
              ) : (
                <div
                  className="price-row-flex"
                  onClick={() => onEditPrice({ chargeCode: 'total' })}
                >
                  <div className="header-name">
                    {formatDollars(getSummary(), { zeroPrice: true })}
                  </div>
                  <PencilSimpleLine size={18} />
                </div>
              )
            ) : (
              <div className="header-name">{formatDollars(getSummary(), { zeroPrice: true })}</div>
            )}
          </div>
          <div className="summary-details__space">
            <div className="header-name__bold">{t('organisation:payments.payNow.summary')}</div>
            <div className="header-name__bold">
              {formatDollars(getSummary(), { zeroPrice: true })}
            </div>
          </div>
          <button
            className="button button-black"
            disabled={!getSummary()}
            onClick={() => setPayView(true)}
          >
            {t('organisation:payments.payNow.choosePaymentMethod')}
          </button>
        </div>
      </div>
      <BillDetailsModal
        isOpen={!!transactionDetails}
        onClose={() => setTransactionDetails(null)}
        isYardi={isYardi}
        data={transactionDetails}
      />
    </Dashboard.Container>
  )
}

PayNowView.propTypes = {
  currentUser: PropTypes.object,
  isYardi: PropTypes.bool,
  yardiTransactions: PropTypes.array,
  selectedTransactions: PropTypes.array,
  editTransaction: PropTypes.object,
  totalPay: PropTypes.string,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func,
  onEditPrice: PropTypes.func,
  handleChangeValue: PropTypes.func,
  saveNewValue: PropTypes.func,
  getOriginalValue: PropTypes.func,
  setTransactionDetails: PropTypes.func,
  manageSelectedTransaction: PropTypes.func,
  paymentSettings: PropTypes.object,
  customTotal: PropTypes.string,
  refetchTransactions: PropTypes.func,
}

export default PayNowView
